import { useEffect, useState } from "react";
import type {
  ResultsSnapshot,
  VotePublic,
  WsServerMessage,
} from "../../shared/types";
import { ApiError, api } from "./api";

export type LiveStatus =
  | "loading"
  | "live"
  | "reconnecting"
  | "notfound"
  | "error";

const MAX_BACKOFF_MS = 15_000;

function isSnapshotShape(value: unknown): value is WsServerMessage {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  if (v.type === "snapshot") {
    return typeof v.vote === "object" && v.vote !== null && isResults(v.results);
  }
  if (v.type === "update") {
    return isResults(v.results);
  }
  return false;
}

function isResults(value: unknown): value is ResultsSnapshot {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    Array.isArray(v.counts) &&
    v.counts.every((n) => typeof n === "number") &&
    typeof v.totalBallots === "number"
  );
}

/**
 * Henter først et øyeblikksbilde over HTTP, og abonnerer deretter på
 * live-oppdateringer over WebSocket med automatisk gjenoppkobling.
 * Hver gjenoppkobling gir et nytt snapshot fra serveren, så oppdateringer
 * som gikk tapt underveis hentes inn igjen.
 */
export function useLiveResults(resultsId: string | undefined) {
  const [vote, setVote] = useState<VotePublic | null>(null);
  const [results, setResults] = useState<ResultsSnapshot | null>(null);
  const [status, setStatus] = useState<LiveStatus>("loading");

  useEffect(() => {
    if (!resultsId) {
      setStatus("notfound");
      return;
    }

    let stopped = false;
    let socket: WebSocket | null = null;
    let timer: number | undefined;
    let attempts = 0;

    const connect = () => {
      if (stopped) return;
      const protocol = location.protocol === "https:" ? "wss" : "ws";
      socket = new WebSocket(
        `${protocol}://${location.host}/ws?results=${encodeURIComponent(resultsId)}`,
      );

      socket.onopen = () => {
        attempts = 0;
        setStatus("live");
      };

      socket.onmessage = (event) => {
        if (typeof event.data !== "string") return;
        let message: unknown;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }
        if (!isSnapshotShape(message)) return;
        if (message.type === "snapshot") {
          setVote(message.vote);
          setResults(message.results);
        } else {
          setResults(message.results);
        }
      };

      socket.onclose = () => {
        if (stopped) return;
        setStatus("reconnecting");
        attempts += 1;
        const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(attempts, 4));
        timer = window.setTimeout(connect, delay);
      };
    };

    api
      .getResults(resultsId)
      .then(({ vote: v, results: r }) => {
        if (stopped) return;
        setVote(v);
        setResults(r);
        connect();
      })
      .catch((error: unknown) => {
        if (stopped) return;
        setStatus(
          error instanceof ApiError && error.status === 404
            ? "notfound"
            : "error",
        );
      });

    return () => {
      stopped = true;
      window.clearTimeout(timer);
      socket?.close();
    };
  }, [resultsId]);

  return { vote, results, status };
}
